import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { type AgentDto, useAgentApi } from "@/api/agents";
import { TeamStatus, useTeamApi } from "@/api/teams";
import { type TicketDetailDto, type TicketDto, useTicketApi } from "@/api/tickets";
import { Loader } from "@/components/motion/loader";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { useI18n } from "@/locales/i18n-provider";

const MAX_RESULT_COUNT = 1000;
const UNASSIGNED_VALUE = "__unassigned__";

type AssignmentAgentOption = Pick<AgentDto, "displayName" | "id" | "userName">;

type TicketAssignmentDialogProps = {
  open: boolean;
  ticket: TicketDetailDto;
  onAssigned: (ticket: TicketDto) => void;
  onOpenChange: (open: boolean) => void;
};

class PartialAssignmentError extends Error {
  readonly updatedTicket: TicketDto;

  constructor(updatedTicket: TicketDto, cause: unknown, fallbackMessage: string) {
    super(cause instanceof Error && cause.message ? cause.message : fallbackMessage);
    this.name = "PartialAssignmentError";
    this.updatedTicket = updatedTicket;
  }
}

function errorMessage(error: unknown, fallbackMessage: string) {
  return error instanceof Error && error.message ? error.message : fallbackMessage;
}

/** 工单分配流程：先路由到客服组，再按该组成员范围指定或清空跟进人。 */
export function TicketAssignmentDialog({ open, ticket, onAssigned, onOpenChange }: TicketAssignmentDialogProps) {
  const { intlLocale, t } = useI18n();
  const ticketApi = useTicketApi();
  const teamApi = useTeamApi();
  const agentApi = useAgentApi();
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [selectedAssigneeId, setSelectedAssigneeId] = useState(UNASSIGNED_VALUE);

  const teamsQuery = useQuery({
    queryKey: ["teams", "ticket-assignment", "active"],
    queryFn: () => teamApi.getList({ maxResultCount: MAX_RESULT_COUNT, onlyActive: true, sorting: "Name" }),
    enabled: open,
    staleTime: 60 * 1000,
  });
  const agentsQuery = useQuery({
    queryKey: ["agents", "ticket-assignment", "active"],
    queryFn: () => agentApi.getList({ maxResultCount: MAX_RESULT_COUNT, onlyActive: true, sorting: "DisplayName" }),
    enabled: open,
    staleTime: 60 * 1000,
  });

  const eligibleTeams = useMemo(
    () =>
      (teamsQuery.data?.items ?? []).filter((team) => team.status === TeamStatus.Active && (team.projectId === null || team.projectId === ticket.projectId)),
    [teamsQuery.data?.items, ticket.projectId],
  );
  const selectedTeam = eligibleTeams.find((team) => team.id === selectedTeamId) ?? null;
  const agentById = useMemo(() => new Map((agentsQuery.data?.items ?? []).map((agent) => [agent.id, agent])), [agentsQuery.data?.items]);
  const assigneeOptions = useMemo(() => {
    if (!selectedTeam) return [];

    const options: AssignmentAgentOption[] = selectedTeam.members
      .map((member) => agentById.get(member.agentId))
      .filter((agent): agent is AgentDto => Boolean(agent))
      .map(({ displayName, id, userName }) => ({ displayName, id, userName }))
      .sort((left, right) => left.displayName.localeCompare(right.displayName, intlLocale));

    if (selectedTeam.id === ticket.assignedTeamId && ticket.assignee && !options.some((agent) => agent.id === ticket.assignee?.agentId)) {
      options.unshift({
        id: ticket.assignee.agentId,
        displayName: ticket.assignee.displayName,
        // 工单快照只留了显示名；这一条不参与"同名分不清"的场景（它就是当前跟进人）。
        userName: "",
      });
    }

    return options;
  }, [agentById, intlLocale, selectedTeam, ticket.assignedTeamId, ticket.assignee]);

  useEffect(() => {
    if (!open) return;
    setSelectedTeamId(ticket.assignedTeamId ?? "");
    setSelectedAssigneeId(ticket.assigneeId ?? UNASSIGNED_VALUE);
  }, [open, ticket.assignedTeamId, ticket.assigneeId]);

  useEffect(() => {
    if (!open || teamsQuery.isPending || !selectedTeamId) return;
    if (!eligibleTeams.some((team) => team.id === selectedTeamId)) {
      setSelectedTeamId("");
      setSelectedAssigneeId(UNASSIGNED_VALUE);
    }
  }, [eligibleTeams, open, selectedTeamId, teamsQuery.isPending]);

  const selectedAssignee = selectedAssigneeId === UNASSIGNED_VALUE ? null : (assigneeOptions.find((agent) => agent.id === selectedAssigneeId) ?? null);
  const nextAssigneeId = selectedAssignee?.id ?? null;
  const teamWillChange = Boolean(selectedTeamId && selectedTeamId !== ticket.assignedTeamId);
  const hasChanges = Boolean(selectedTeamId) && (selectedTeamId !== ticket.assignedTeamId || nextAssigneeId !== ticket.assigneeId);
  const optionsLoading = teamsQuery.isPending || agentsQuery.isPending;
  const optionsError = teamsQuery.isError || agentsQuery.isError;

  const assignmentMutation = useMutation({
    mutationFn: async ({ assigneeId, teamId }: { assigneeId: string | null; teamId: string }) => {
      let updated: TicketDto = ticket;
      const teamChanged = teamId !== ticket.assignedTeamId;

      if (teamChanged) {
        updated = await ticketApi.route(ticket.id, { teamId, concurrencyStamp: updated.concurrencyStamp });
      }

      try {
        if (assigneeId && (teamChanged || assigneeId !== ticket.assigneeId)) {
          updated = await ticketApi.assign(ticket.id, { assigneeId, concurrencyStamp: updated.concurrencyStamp });
        } else if (!teamChanged && !assigneeId && ticket.assigneeId) {
          updated = await ticketApi.unassign(ticket.id, { concurrencyStamp: updated.concurrencyStamp });
        }
      } catch (error) {
        if (teamChanged) throw new PartialAssignmentError(updated, error, t("tickets.assignment.assigneeFailed"));
        throw error;
      }

      return updated;
    },
    onSuccess: (updated) => {
      onAssigned(updated);
      onOpenChange(false);
      toast.success(
        selectedAssignee
          ? t("tickets.assignment.assignedTo", { assignee: selectedAssignee.displayName })
          : t("tickets.assignment.assignedToTeam", { team: selectedTeam?.name ?? t("tickets.assignment.teamFallback") }),
      );
    },
    onError: (error) => {
      if (error instanceof PartialAssignmentError) {
        onAssigned(error.updatedTicket);
        toast.error(t("tickets.assignment.partialError", { message: error.message }));
        return;
      }
      toast.error(errorMessage(error, t("tickets.assignment.failed")));
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (!assignmentMutation.isPending) onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent showCloseButton={!assignmentMutation.isPending}>
        <DialogHeader>
          <DialogTitle>{t("tickets.assignment.dialogTitle")}</DialogTitle>
          <DialogDescription>{t("tickets.assignment.dialogDescription")}</DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field data-disabled={assignmentMutation.isPending || undefined}>
            <FieldLabel htmlFor="ticket-assignment-team">{t("tickets.assignment.team")}</FieldLabel>
            <Select
              disabled={assignmentMutation.isPending || optionsError}
              value={selectedTeamId}
              onValueChange={(teamId) => {
                setSelectedTeamId(teamId);
                setSelectedAssigneeId(teamId === ticket.assignedTeamId ? (ticket.assigneeId ?? UNASSIGNED_VALUE) : UNASSIGNED_VALUE);
              }}
            >
              <SelectTrigger id="ticket-assignment-team" className="w-full" loading={teamsQuery.isPending} loadingLabel={t("tickets.assignment.loadingTeams")}>
                <SelectValue placeholder={t("tickets.assignment.selectTeam")} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {eligibleTeams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {teamWillChange && ticket.assignee ? (
              <FieldDescription>{t("tickets.assignment.willUnassign", { assignee: ticket.assignee.displayName })}</FieldDescription>
            ) : !teamsQuery.isPending && !eligibleTeams.length && !teamsQuery.isError ? (
              <FieldDescription>{t("tickets.assignment.noEligibleTeams")}</FieldDescription>
            ) : null}
          </Field>

          <Field data-disabled={!selectedTeam || assignmentMutation.isPending || undefined}>
            <FieldLabel htmlFor="ticket-assignment-assignee">{t("tickets.fields.assignee")}</FieldLabel>
            <Select disabled={!selectedTeam || assignmentMutation.isPending || optionsError} value={selectedAssigneeId} onValueChange={setSelectedAssigneeId}>
              <SelectTrigger
                id="ticket-assignment-assignee"
                className="w-full"
                loading={Boolean(selectedTeam) && agentsQuery.isPending}
                loadingLabel={t("tickets.assignment.loadingMembers")}
              >
                <SelectValue placeholder={t(selectedTeam ? "tickets.assignment.selectAssignee" : "tickets.assignment.selectTeamFirst")} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={UNASSIGNED_VALUE}>{t("tickets.assignment.noAssignee")}</SelectItem>
                  {assigneeOptions.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.displayName}
                      {agent.userName ? <span className="ml-2 text-xs text-muted-foreground">{agent.userName}</span> : null}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {selectedTeam && !agentsQuery.isPending && !assigneeOptions.length ? (
              <FieldDescription>{t("tickets.assignment.noEligibleMembers")}</FieldDescription>
            ) : null}
          </Field>

          {optionsError ? <FieldError>{t("tickets.assignment.optionsError")}</FieldError> : null}
        </FieldGroup>

        <DialogFooter>
          <Button disabled={assignmentMutation.isPending} type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            {t("tickets.actions.cancel")}
          </Button>
          <Button
            disabled={!hasChanges || optionsLoading || optionsError || assignmentMutation.isPending}
            type="button"
            onClick={() => assignmentMutation.mutate({ assigneeId: nextAssigneeId, teamId: selectedTeamId })}
          >
            {assignmentMutation.isPending ? <Loader className="text-current" label={t("tickets.assignment.saving")} size={16} /> : null}
            {t("tickets.assignment.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
